import { useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FAMILY_ORIGIN_LABELS,
  FAMILY_STATUS_LABELS,
  FAMILY_STATUSES,
  hasAtLeastRole,
  type ArticleFamily,
  type DecideFamilyInput,
  type FamilyExtension,
  type FamilyStatus,
} from '@kn/shared';
import { api, type FamilyDetectionSummary } from '../../api/client';
import { messageErreur } from '../../api/error-message';
import { FamilleFabrications } from './FamilleFabrications';
import { useAuth } from '../../auth/auth-context';

const FAMILIES_QUERY_KEY = ['families'] as const;

/** Motifs pour lesquels une fabrication n'a produit aucune proposition. */
const MOTIFS_ECART: Record<string, string> = {
  PLUSIEURS_COMPOSANTS: 'Plusieurs composants (vrai copacking)',
  AUCUN_COMPOSANT: 'Aucun composant',
  QUANTITES_DIFFERENTES: 'Quantités non equivalentes',
  QUANTITE_NON_POSITIVE: 'Quantité nulle ou negative',
  MEME_CODE: 'Compose et composant identiques',
  CODE_DEJA_EN_FAMILLE: 'Code déjà rattache à une famille',
  COMPOSE_HORS_PLAGE: 'Composé hors plage 4/5xxxxx',
  COMPOSANT_HORS_PLAGE: 'Composant hors plage 4/5xxxxx (matière sèche, goodies)',
  DEJA_COUVERTE: 'Grappe déjà couverte par une famille',
  FAMILLES_MULTIPLES: 'Grappe à cheval sur deux familles — fusion à décider à la main',
};

/**
 * Bilan de la derniere detection. Les cas non concluants sont listes : la regle
 * 1:1 ne tranche pas a la place de l'utilisateur, elle lui rend la main.
 */
function BilanDetection({ bilan }: { bilan: FamilyDetectionSummary }): ReactElement {
  const parMotif = new Map<string, number>();
  for (const cas of bilan.skipped) {
    parMotif.set(cas.reason, (parMotif.get(cas.reason) ?? 0) + 1);
  }

  const parMotifCopacking = new Map<string, number>();
  for (const cas of bilan.copacking?.ignores ?? []) {
    parMotifCopacking.set(cas.raison, (parMotifCopacking.get(cas.raison) ?? 0) + 1);
  }

  return (
    <div className="carte">
      <h2>Bilan de la détection</h2>
      <h3>Jumeaux — fabrications FAB 1:1</h3>
      <p className="sous-titre">
        {bilan.fabricationsAnalysees} fabrication(s) analysée(s) &middot;{' '}
        {bilan.createdFamilyIds.length} proposition(s) créée(s) &middot;{' '}
        {bilan.ignoredProposals.length} ignorée(s) (code déjà rattache) &middot;{' '}
        {bilan.skipped.length} fabrication(s) non concluante(s). Les propositions restent à valider
        une par une.
      </p>
      {parMotif.size > 0 && (
        <ul>
          {[...parMotif].map(([motif, nombre]) => (
            <li key={motif}>
              {MOTIFS_ECART[motif] ?? motif} : {nombre}
            </li>
          ))}
        </ul>
      )}

      <h3>Grappes copacking — tables Copacking et Nomenclature</h3>
      <p className="sous-titre">
        {bilan.copacking?.liensAnalyses ?? 0} lien(s) déclaré(s) &middot;{' '}
        {bilan.copacking?.createdFamilyIds.length ?? 0} grappe(s) proposée(s) &middot;{' '}
        {bilan.copacking?.createdExtensionIds.length ?? 0} extension(s) de famille proposée(s)
        &middot; {bilan.copacking?.ignores.length ?? 0} grappe(s) écartée(s). Le composé n&apos;existe
        pas en comptabilité matières : la grappe &#123;composé + composants&#125; est la seule
        unité de comparaison possible. Comme les jumeaux, elle se valide à la main.
      </p>
      {parMotifCopacking.size > 0 && (
        <ul>
          {[...parMotifCopacking].map(([motif, nombre]) => (
            <li key={motif}>
              {MOTIFS_ECART[motif] ?? motif} : {nombre}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Extensions proposées d'une famille existante.
 *
 * Une grappe de copacking dont un code est déjà rattaché ne peut pas être
 * proposée comme famille neuve. La jeter perdrait ce que la table déclare :
 * c'est la famille existante qu'il faut élargir, à la main comme le reste.
 */
function ExtensionsProposees({
  extensions,
  peutModifier,
  onDecision,
}: {
  extensions: FamilyExtension[];
  peutModifier: boolean;
  onDecision: (id: string, decision: 'VALIDER' | 'REJETER') => void;
}): ReactElement {
  return (
    <div className="carte">
      <h2>Extensions proposées ({extensions.length})</h2>
      <p className="sous-titre">
        Les tables de copacking déclarent une grappe dont une partie appartient déjà à une famille.
        L&apos;outil propose de l&apos;élargir ; il ne l&apos;élargit jamais seul. Valider ajoute
        uniquement les codes encore libres.
      </p>
      <div className="tableau-defilant">
        <table className="tableau">
          <thead>
            <tr>
              <th scope="col">Famille visée</th>
              <th scope="col">Membres actuels</th>
              <th scope="col">Codes à ajouter</th>
              <th scope="col">Motif</th>
              <th scope="col">Décision</th>
            </tr>
          </thead>
          <tbody>
            {extensions.map((extension) => (
              <tr key={extension.id}>
                <td>
                  {extension.familleLibelle ?? extension.familyId}
                  <div className="sous-titre">
                    {FAMILY_STATUS_LABELS[extension.familleStatut]} &middot;{' '}
                    {FAMILY_ORIGIN_LABELS[extension.origine]}
                  </div>
                </td>
                <td>{extension.membresActuels.join(' / ')}</td>
                <td>
                  <strong>{extension.codpros.join(' / ')}</strong>
                </td>
                <td>{extension.motif ?? '—'}</td>
                <td>
                  {peutModifier ? (
                    <div className="barre-actions__boutons">
                      <button
                        type="button"
                        className="bouton bouton--secondaire"
                        onClick={() => onDecision(extension.id, 'VALIDER')}
                      >
                        Étendre
                      </button>
                      <button
                        type="button"
                        className="bouton bouton--lien"
                        onClick={() => onDecision(extension.id, 'REJETER')}
                      >
                        Rejeter
                      </button>
                    </div>
                  ) : (
                    <span className="sous-titre">Lecture seule</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function FamiliesPage(): ReactElement {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const peutModifier = user ? hasAtLeastRole(user.role, 'OPERATEUR') : false;

  const [statut, setStatut] = useState<FamilyStatus | 'tous'>('tous');
  const [recherche, setRecherche] = useState('');
  const [formulaireOuvert, setFormulaireOuvert] = useState(false);
  const [libelle, setLibelle] = useState('');
  const [codprosSaisis, setCodprosSaisis] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  /** Famille dont on déplie les fabrications, en vue composé → composants. */
  const [fabrications, setFabrications] = useState<string | null>(null);

  const [message, setMessage] = useState<string | null>(null);

  const listeQuery = useQuery({
    queryKey: [...FAMILIES_QUERY_KEY, statut, recherche],
    queryFn: () =>
      api.listFamilies({
        statut: statut === 'tous' ? undefined : statut,
        search: recherche || undefined,
        pageSize: 200,
      }),
  });

  const extensionsQuery = useQuery({
    queryKey: [...FAMILIES_QUERY_KEY, 'extensions'],
    queryFn: () => api.listFamilyExtensions('PROPOSEE'),
  });

  function rafraichir(texte: string): void {
    setErreur(null);
    setMessage(texte);
    void queryClient.invalidateQueries({ queryKey: FAMILIES_QUERY_KEY });
  }

  const decisionExtension = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'VALIDER' | 'REJETER' }) =>
      api.decideFamilyExtension(id, { decision }),
    onSuccess: (extension) =>
      rafraichir(
        extension.statut === 'VALIDEE'
          ? `Famille élargie : ${extension.codpros.join(' / ')}.`
          : 'Extension rejetée.',
      ),
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  const creation = useMutation({
    mutationFn: (codpros: string[]) =>
      api.createFamily({ libelle: libelle.trim() || null, codpros, statut: 'VALIDEE' }),
    onSuccess: () => {
      setLibelle('');
      setCodprosSaisis('');
      setFormulaireOuvert(false);
      rafraichir('Famille créée et validée.');
    },
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  const decision = useMutation({
    mutationFn: ({ id, body }: { id: string; body: DecideFamilyInput }) =>
      api.decideFamily(id, body),
    onSuccess: (famille) =>
      rafraichir(famille.statut === 'VALIDEE' ? 'Proposition validée.' : 'Proposition rejetée.'),
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  const [detection, setDetection] = useState<FamilyDetectionSummary | null>(null);

  const lancerDetection = useMutation({
    mutationFn: () => api.runFamilyDetection(),
    onSuccess: (bilan) => {
      setDetection(bilan);
      rafraichir(
        `Détection terminée : ${bilan.createdFamilyIds.length} proposition(s) créée(s) sur ${bilan.fabricationsAnalysees} fabrication(s) analysée(s).`,
      );
    },
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  const desactivation = useMutation({
    mutationFn: (id: string) => api.deactivateFamily(id),
    onSuccess: () =>
      rafraichir(
        "Famille désactivée : elle n'agrège plus, ses articles reprennent leur ligne. " +
          'Les écarts ont été recalculés.',
      ),
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  const rattrapage = useMutation({
    mutationFn: () => api.runFamilyRattrapage(),
    onSuccess: (bilan) =>
      rafraichir(
        `Rattrapage terminé : ${bilan.grappesCreees + bilan.grappesAnciennesValidees} grappe(s) ` +
          `de copacking et ${bilan.fabCreeesAutoValidees + bilan.fabAnciennesValidees} famille(s) ` +
          `FAB validées automatiquement, ${bilan.extensionsAppliquees} extension(s) appliquée(s). ` +
          `${bilan.resteesProposees} proposition(s) restent à trancher.`,
      ),
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  const suppression = useMutation({
    mutationFn: (id: string) => api.deleteFamily(id),
    onSuccess: () => rafraichir('Famille supprimée, ses codes sont à nouveau disponibles.'),
    onError: (error: unknown) => setErreur(messageErreur(error)),
  });

  function handleCreation(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setErreur(null);
    setMessage(null);

    const codpros = codprosSaisis
      .split(/[\s,;]+/)
      .map((code) => code.trim())
      .filter((code) => code !== '');

    if (codpros.length < 2) {
      setErreur('Une famille doit comporter au moins deux codes articles.');
      return;
    }
    creation.mutate(codpros);
  }

  function handleDesactivation(famille: ArticleFamily): void {
    setErreur(null);
    setMessage(null);
    const confirme = window.confirm(
      `Désactiver la famille ${famille.members.map((m) => m.codpro).join(' / ')} ?

` +
        "Elle cesse d'agréger : ses articles reviennent en lignes séparées et les écarts sont " +
        "recalculés aussitôt. Rien n'est supprimé, l'action est tracée.",
    );
    if (confirme) {
      desactivation.mutate(famille.id);
    }
  }

  function handleSuppression(famille: ArticleFamily): void {
    setErreur(null);
    setMessage(null);
    const confirme = window.confirm(
      `Supprimer la famille ${famille.members.map((m) => m.codpro).join(' / ')} ?\n\n` +
        "Ses codes redeviendront rattachables à une autre famille. L'action est tracée.",
    );
    if (confirme) {
      suppression.mutate(famille.id);
    }
  }

  const familleDepliee =
    listeQuery.data?.items.find((famille) => famille.id === fabrications) ?? null;

  const enCours =
    creation.isPending ||
    decision.isPending ||
    suppression.isPending ||
    lancerDetection.isPending ||
    desactivation.isPending ||
    rattrapage.isPending;

  return (
    <>
      <h1>Référentiels &mdash; Familles d&apos;équivalence</h1>
      <p className="sous-titre">
        Codes jumeaux&nbsp;: plusieurs codes articles designant le même article physique. Les
        propositions issues de la détection automatique ne sont jamais actives sans validation
        humaine. Un code article n&apos;appartient qu&apos;a une seule famille.
      </p>

      {erreur && (
        <p className="alerte alerte--erreur" role="alert">
          {erreur}
        </p>
      )}
      {message && <p className="alerte alerte--succes">{message}</p>}

      {detection && <BilanDetection bilan={detection} />}

      {extensionsQuery.data && extensionsQuery.data.length > 0 && (
        <ExtensionsProposees
          extensions={extensionsQuery.data}
          peutModifier={peutModifier}
          onDecision={(id, decision) => decisionExtension.mutate({ id, decision })}
        />
      )}

      <div className="carte">
        <div className="barre-actions">
          <label className="champ" htmlFor="recherche-famille">
            <span>Rechercher</span>
            <input
              id="recherche-famille"
              type="search"
              placeholder="Code article ou libellé"
              value={recherche}
              onChange={(event) => setRecherche(event.target.value)}
            />
          </label>
          <label className="champ" htmlFor="filtre-statut">
            <span>Statut</span>
            <select
              id="filtre-statut"
              value={statut}
              onChange={(event) => setStatut(event.target.value as FamilyStatus | 'tous')}
            >
              <option value="tous">Tous les statuts</option>
              {FAMILY_STATUSES.map((valeur) => (
                <option key={valeur} value={valeur}>
                  {FAMILY_STATUS_LABELS[valeur]}
                </option>
              ))}
            </select>
          </label>
          {peutModifier && (
            <>
              <button
                type="button"
                className="bouton"
                onClick={() => setFormulaireOuvert((ouvert) => !ouvert)}
              >
                {formulaireOuvert ? 'Annuler' : 'Nouvelle famille'}
              </button>
              <button
                type="button"
                className="bouton bouton--secondaire"
                disabled={enCours}
                onClick={() => lancerDetection.mutate()}
              >
                {lancerDetection.isPending ? 'Détection en cours...' : 'Lancer la détection'}
              </button>
              <button
                type="button"
                className="bouton bouton--secondaire"
                disabled={enCours}
                title="Applique la règle d'auto-validation aux propositions déjà en attente, puis recalcule les écarts."
                onClick={() => rattrapage.mutate()}
              >
                {rattrapage.isPending ? 'Rattrapage en cours...' : 'Rattraper les propositions'}
              </button>
            </>
          )}
        </div>

        {formulaireOuvert && peutModifier && (
          <form onSubmit={handleCreation} noValidate>
            <div className="barre-actions">
              <label className="champ" htmlFor="famille-libelle">
                <span>Libellé (facultatif)</span>
                <input
                  id="famille-libelle"
                  type="text"
                  value={libelle}
                  onChange={(event) => setLibelle(event.target.value)}
                />
              </label>
              <label className="champ" htmlFor="famille-codpros">
                <span>Codes articles</span>
                <input
                  id="famille-codpros"
                  type="text"
                  placeholder="435173 436110"
                  value={codprosSaisis}
                  onChange={(event) => setCodprosSaisis(event.target.value)}
                />
              </label>
              <button type="submit" className="bouton" disabled={enCours}>
                Créer la famille
              </button>
            </div>
            <p className="sous-titre">
              Separez les codes par un espace, une virgule ou un point-virgule. Une famille saisie
              manuellement est directement validée.
            </p>
          </form>
        )}

        {listeQuery.isPending && <p className="chargement">Chargement des familles...</p>}
        {listeQuery.isError && (
          <p className="alerte alerte--erreur">{messageErreur(listeQuery.error)}</p>
        )}

        {listeQuery.data && listeQuery.data.total === 0 && (
          <p className="alerte alerte--info">
            Aucune famille enregistree. Les propositions automatiques apparaitront après
            l&apos;import des mouvements de copacking.
          </p>
        )}

        {listeQuery.data && listeQuery.data.total > 0 && (
          <table className="tableau">
            <caption className="sous-titre">{listeQuery.data.total} famille(s)</caption>
            <thead>
              <tr>
                <th scope="col">Codes articles</th>
                <th scope="col">Libellé</th>
                <th scope="col">Statut</th>
                <th scope="col">Origine</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {listeQuery.data.items.map((famille) => (
                <tr key={famille.id}>
                  <td>
                    {famille.members.map((membre) => (
                      <div key={membre.codpro}>
                        <strong>{membre.codpro}</strong>
                        {membre.designation ? ` — ${membre.designation}` : ' — (hors référentiel)'}
                      </div>
                    ))}
                  </td>
                  <td>{famille.libelle ?? '—'}</td>
                  <td>
                    <span
                      className={`etiquette ${
                        famille.statut === 'VALIDEE'
                          ? 'etiquette--actif'
                          : famille.statut === 'REJETEE'
                            ? 'etiquette--inactif'
                            : ''
                      }`}
                    >
                      {FAMILY_STATUS_LABELS[famille.statut]}
                    </span>
                    {famille.validationAuto && famille.motifValidation && (
                      <div className="sous-titre" title={famille.motifValidation}>
                        {famille.motifValidation}
                      </div>
                    )}
                  </td>
                  <td>{FAMILY_ORIGIN_LABELS[famille.origine]}</td>
                  <td>
                    <button
                      type="button"
                      className="bouton bouton--lien"
                      onClick={() =>
                        setFabrications(fabrications === famille.id ? null : famille.id)
                      }
                    >
                      {fabrications === famille.id ? 'Masquer' : 'Fabrications'}
                    </button>
                    {' | '}
                    {peutModifier && famille.statut === 'PROPOSEE' && (
                      <>
                        <button
                          type="button"
                          className="bouton bouton--lien"
                          disabled={enCours}
                          onClick={() =>
                            decision.mutate({ id: famille.id, body: { decision: 'VALIDER' } })
                          }
                        >
                          Valider
                        </button>
                        {' | '}
                        <button
                          type="button"
                          className="bouton bouton--lien"
                          disabled={enCours}
                          onClick={() =>
                            decision.mutate({ id: famille.id, body: { decision: 'REJETER' } })
                          }
                        >
                          Rejeter
                        </button>
                        {' | '}
                      </>
                    )}
                    {peutModifier && famille.statut === 'VALIDEE' && (
                      <>
                        <button
                          type="button"
                          className="bouton bouton--lien"
                          disabled={enCours}
                          onClick={() => handleDesactivation(famille)}
                        >
                          Désactiver
                        </button>
                        {' | '}
                      </>
                    )}
                    {peutModifier ? (
                      <button
                        type="button"
                        className="bouton bouton--lien"
                        disabled={enCours}
                        onClick={() => handleSuppression(famille)}
                      >
                        Supprimer
                      </button>
                    ) : (
                      <span className="sous-titre">Lecture seule</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {familleDepliee && <FamilleFabrications famille={familleDepliee} />}
    </>
  );
}
